import React from 'react';
import {
    Ad,
    AppContent,
    baseUnit,
    DisableMainCategory,
    ErrorBoundary,
    FourHC,
    PaginationButtonGroup,
    Section,
    SectionOnTopTitle,
    ThreeVC,
    TrianglesIconForDivider,
} from 'mm-ui-components';
import { Store } from 'redux';
import { useSelector } from 'react-redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { Page } from '../Page';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import {
    getFeedSectionArticles,
    getMetadataDescription,
    getMetadataImage,
    getMetadataTitle,
    getNextButtonText,
    getNextButtonURL,
    getPrevButtonText,
    getPrevButtonURL,
    getTopSectionImage,
    getTopSectionSubtitle,
    getTopSectionTitle,
    getHreflangData,
} from '../../store/template/feedPage/feedPageNairobi/feedPageNairobi.selectors';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import { getAdSlotId, getAdSlotOnSuccess } from '../../store/config/config.selectors';
import { SLOT_NAMES } from '../templates.const';

interface FeedPageProps {
    store: Store<AppState, AppActions>;
    errorProvider: ErrorReporter;
}

const createStyle = () => StyleSheet.create({
    sectionsWrapper: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridRowGap: `${baseUnit}px`,
        justifyItems: 'center',
        marginTop: `${baseUnit * 0.25}px`,
    },
});

export const FeedPageNairobiComponent: React.FunctionComponent<FeedPageProps> = ({ store, errorProvider }) => {
    useMeasureHydrateTime();
    return (
      <Page store={store} errorProvider={errorProvider}>
        <ErrorBoundary>
          <FeedPageNairobiContent />
        </ErrorBoundary>
      </Page>
    );
};

export const FeedPageNairobiContent: React.FunctionComponent = () => {
    const prevButtonText = useSelector(getPrevButtonText);
    const prevButtonURL = useSelector(getPrevButtonURL);
    const nextButtonText = useSelector(getNextButtonText);
    const nextButtonURL = useSelector(getNextButtonURL);
    const metaTitle = useSelector(getMetadataTitle);
    const metaDescription = useSelector(getMetadataDescription);
    const pageImage = useSelector(getMetadataImage);
    const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
    const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
    const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
    const oneOnOneOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.ONE_ON_ONE));
    const topSectionTitle = useSelector(getTopSectionTitle);
    const topSectionSubtitle = useSelector(getTopSectionSubtitle);
    const topSectionImage = useSelector(getTopSectionImage);
    const articles = useSelector(getFeedSectionArticles) || [];
    const hreflangData = useSelector(getHreflangData) || [];
    const style = createStyle();
    return (
      <React.Fragment>
        <MetaTags articles={articles} title={metaTitle} description={metaDescription} pageImage={pageImage} alternateUrls={hreflangData} />
        <HeadScripts />
        <Theme>
          <DisableMainCategory>
            <AppContent>
              <NavigationLayout mainWrapperConfigThemeKey="mainContent">
                <Ad id={topAdId} onSuccess={topAdOnSuccess} setMinHeight />
                <div className={css(style.sectionsWrapper)}>

                  <Section themeConfigurationPropKey="firstSectionMaxWidth">
                    <SectionOnTopTitle title={topSectionTitle} subtitle={topSectionSubtitle} image={topSectionImage} />
                  </Section>

                  <Section>
                    <ThreeVC cards={articles.slice(0, 3)} paddingFactors={{ large: 1, medium: 1, small: 0.5 }} />
                  </Section>

                  <Section>
                    <FourHC cards={articles.slice(3)} showDivider dividerIcon={TrianglesIconForDivider} paddingFactors={{ large: 1, medium: 1, small: 0.5 }} />
                  </Section>
                </div>
                <PaginationButtonGroup prevText={prevButtonText} prevURL={prevButtonURL} nextText={nextButtonText} nextURL={nextButtonURL} />
                <Ad id={oneOnOneId} onSuccess={oneOnOneOnSuccess} />
              </NavigationLayout>
            </AppContent>
          </DisableMainCategory>
        </Theme>
      </React.Fragment>
    );
};
